import type { FC } from 'react';
import React, { useContext, useState, useCallback } from 'react';
import styled from 'styled-components';
import type { Portfolio } from 'venn-api';
import {
  recursiveUpdatePortfolioName,
  recursiveScaleAssets,
  recursiveClearBenchmarks,
  logMessageToSentry,
  assertNotNil,
} from 'venn-utils';
import { Modal, ModalFooter, ModalHeader, ModalContent } from '../../modal';
import { ConditionalOverlay } from '../../conditional-overlay';
import PortfoliosContext from '../../contexts/portfolios-context';

interface UpdatePortfolioModalProps {
  portfolio: Portfolio;
  isPercentageMode: boolean;
  total: number | undefined;
  clearBenchmarksBeforeSaving?: boolean;
  onClose: () => void;
  onSubmitCallback?: (newPortfolio: Portfolio | undefined) => void;
  openSaveNewPortfolioModal: () => void;
}

const UpdatePortfolioModal: FC<React.PropsWithChildren<UpdatePortfolioModalProps>> = ({
  onClose,
  portfolio,
  onSubmitCallback,
  openSaveNewPortfolioModal,
  isPercentageMode,
  total,
  clearBenchmarksBeforeSaving,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const portfoliosContext = useContext(PortfoliosContext);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      let updatedPortfolio = recursiveUpdatePortfolioName(portfolio);
      if (isPercentageMode && total !== portfolio.allocation && portfolio.allocation) {
        updatedPortfolio = recursiveScaleAssets(updatedPortfolio, assertNotNil(total) / portfolio.allocation);
      }
      if (clearBenchmarksBeforeSaving) {
        updatedPortfolio = recursiveClearBenchmarks(updatedPortfolio);
      }
      const saved = await portfoliosContext.updateMaster(updatedPortfolio);
      onSubmitCallback?.(saved);
    } catch (error) {
      const awaitedError = await error;
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logMessageToSentry(awaitedError);
      onSubmitCallback?.(undefined);
    }

    setSubmitting(false);
    onClose();
  }, [onClose, portfolio, isPercentageMode, total, portfoliosContext, onSubmitCallback, clearBenchmarksBeforeSaving]);

  return (
    <Modal onClose={onClose}>
      <ConditionalOverlay condition={submitting}>
        <ModalHeader extraPadding>Update Master</ModalHeader>
        <ModalContent extraPadding>Are you sure you want to overwrite your master portfolio?</ModalContent>
        <StyledFooter
          onCancel={onClose}
          primaryLabel="Yes, overwrite portfolio"
          primaryClassName="qa-create-portfolio"
          onPrimaryClick={onSubmit}
          secondaryLabel="No, save as new portfolio"
          secondaryClassName="qa-create-new"
          onSecondaryClick={openSaveNewPortfolioModal}
        />
      </ConditionalOverlay>
    </Modal>
  );
};

export default UpdatePortfolioModal;

// Fix IE
const StyledFooter = styled(ModalFooter)`
  .qa-create-portfolio,
  .qa-create-new {
    width: 210px;
  }
`;
